import { useLocation } from "react-router-dom";
import { getBrand } from "src/brand/brand";

interface PageMeta {
	title: string;
	description?: string;
	/**
	 * When true, neither the shared desktop `AppLayout` title block
	 * nor the `MobileHeader` heading are rendered on this route — but
	 * `title` is still used for `document.title`. The page is then
	 * expected to provide its own top-level heading. Used by the home
	 * page, which shows a personalised greeting instead of a static
	 * "<Product> Admin" title.
	 */
	hideTitle?: boolean;
}

const pageMetaMap: Record<string, PageMeta> = {
	"/admin": { title: getBrand().adminName, hideTitle: true },
	"/admin/operations/forms": {
		title: "Forms",
		description:
			"Create, edit and manage observation forms that teams use to record assessments."
	},
	"/admin/operations/reports": {
		title: "Reports",
		description:
			"View reporting and analytics across areas, regions, stores and individuals."
	},
	"/admin/vendor-sponsorship/homepage-carousel": {
		title: "Carousel",
		description:
			"Manage the rotating banner carousel displayed on the app and website homepage."
	},
	"/admin/vendor-sponsorship/full-screen-takeover": {
		title: "Takeover",
		description:
			"Manage full screen takeover banners that display when users open the app or website."
	},
	"/admin/vendor-sponsorship/promotion": {
		title: "Promotions",
		description:
			"Manage promotional banners displayed on the app and website homepage."
	},
	"/admin/vendor-sponsorship/app-branding": {
		title: "Branding",
		description:
			"Change the core background colour of the app and website, largely affecting the homepage."
	},
	"/admin/app/homepage/quick-links": {
		title: "Quick Links",
		description:
			"Manage quick link tiles displayed on the app and website homepage."
	},
	"/admin/app/homepage/key-shouts": {
		title: "Key Shouts",
		description:
			"Manage key shout tiles displayed on the app and website homepage."
	},
	"/admin/app/homepage/ordering": {
		title: "Ordering",
		description:
			"Control which sections appear on the app and website homepage and their display order."
	},
	"/admin/app/homepage/section-titles": {
		title: "Section Titles",
		description:
			"Manage the display titles and visibility of homepage sections."
	},
	"/admin/app/events": {
		title: "Events",
		description: "Create and manage events displayed in the app and website."
	},
	"/admin/app/pages": {
		title: "Pages",
		description: "Create and manage standalone content pages."
	},
	"/admin/app/news": {
		title: "News",
		description: "Write and schedule news articles for the app and website."
	},
	"/admin/social-feed/hashtags": {
		title: "Hashtags",
		description: "Create and manage hashtags for the social feed."
	},
	"/admin/social-feed/favourite-feeds": {
		title: "Favourites",
		description:
			"Control which hashtags appear in the favourites feed bar and their display order."
	},
	"/admin/social-feed": {
		title: "Social Feed",
		description: "View the social feed."
	},
	"/admin/recognition/types": {
		title: "Recognition Types",
		description: "Create and manage the recognition types people can send."
	},
	"/admin/recognition/settings": {
		title: "Recognition Settings",
		description:
			"Turn Recognition on or off, and customise its notification copy and compose prompt."
	},
	"/admin/recognition": {
		title: "Recognitions",
		description: "See who's celebrating who across the organisation."
	},
	"/admin/analytics": {
		title: "Analytics",
		description:
			"App and website usage, content performance, and adoption metrics."
	},
	"/admin/announcements": {
		title: "Announcements",
		description:
			"Create and manage announcements sent to app and website users via push notification."
	},
	"/admin/app/navigation": {
		title: "Navigation",
		description: "Manage the menu structure for the app and website."
	},
	"/admin/users/sync/conflicts": {
		title: "Sync Conflicts",
		description:
			"Review and resolve accounts the directory sync could not write."
	},
	"/admin/users/sync/runs": {
		title: "Sync Run",
		description: "What one directory sync run did, and why."
	},
	"/admin/users/sync": {
		title: "Directory Sync",
		description:
			"Create and maintain user accounts from the configured Entra ID groups."
	},
	"/admin/users/people": {
		title: "People",
		description: "View and manage all users across the organisation."
	},
	"/admin/users/groups": {
		title: "Groups",
		description:
			"Create and manage groups for permissions and content targeting."
	},
	"/admin/users/locations": {
		title: "Locations",
		description:
			"Manage the organisational hierarchy of regions, areas, and stores."
	},
	"/admin/users/permissions": {
		title: "Permissions",
		description:
			"Create and manage permission groups that control what users can access."
	}
};

function matchPageMeta(pathname: string): PageMeta {
	// Exact match first
	if (pageMetaMap[pathname]) return pageMetaMap[pathname];

	// Try prefix match (for nested routes like /observations/submissions/123)
	const match = Object.entries(pageMetaMap)
		.filter(([path]) => path !== "/admin")
		.sort(([a], [b]) => b.length - a.length)
		.find(([path]) => pathname.startsWith(path));

	return match ? match[1] : { title: getBrand().productName };
}

/**
 * Single source of truth for route metadata. Resolves once per render
 * — prefer this over the individual `usePage*` hooks when reading
 * more than one field, so route lookup and `useLocation()` only run
 * once per consumer.
 */
export function usePageMeta(): PageMeta {
	const { pathname } = useLocation();
	return matchPageMeta(pathname);
}

export function usePageTitle(): string {
	return usePageMeta().title;
}

export function usePageDescription(): string | undefined {
	return usePageMeta().description;
}

export function useHidePageTitle(): boolean {
	return usePageMeta().hideTitle ?? false;
}
